"use client";

import Image from "next/image";
import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import LocationCard from "./LocationCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { motion } from "framer-motion";
import Fade from "../common/ScrollReveal";
import { useToast } from "@/contexts/ToastContext";
import { z } from "zod";
import { useFormStatus } from 'react-dom';
import { startTransition, useActionState } from 'react';
import { sendEmail } from '@/app/action';
import { createContactUs } from '@/services/api/contactUsApis';
import { useRouter } from "next/navigation";

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" variant="primaryBtn" disabled={pending}>
      {pending ? 'Sending...' : 'Send Message'}
    </Button>
  );
}

// Zod validation schema
const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required").min(2, "Name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
  phone: z.string()
    .min(1, "Phone number is required")
    .refine(
      (val) => {
        // Remove spaces, dashes, and other non-digit characters except +
        const cleaned = val.replace(/[\s\-()]/g, '');

        // UAE phone number patterns:
        // With country code: +971XXXXXXXXX (13 chars) or +971 XX XXX XXXX
        // Without country code: 0XXXXXXXXX (10 digits for mobile) or 0XXXXXXXX (9 digits for landline)
        // Also accept 971XXXXXXXXX (12 digits without +)
        const uaePatterns = [
          /^\+971[0-9]{9}$/, // +971XXXXXXXXX (mobile/landline with country code)
          /^971[0-9]{9}$/,    // 971XXXXXXXXX (without + sign)
          /^0[0-9]{8,9}$/,    // 0XXXXXXXXX (UAE local format, 9-10 digits)
          /^[5][0-9]{8}$/,    // 5XXXXXXXX (mobile without leading 0)
        ];

        // Indian phone number patterns:
        // With country code: +91XXXXXXXXXX (10 digits after +91)
        // Without country code: XXXXXXXXXX (10 digits starting with 6, 7, 8, or 9)
        const indianPatterns = [
          /^\+91[6-9][0-9]{9}$/, // +91XXXXXXXXXX (with country code)
          /^91[6-9][0-9]{9}$/,   // 91XXXXXXXXXX (without + sign)
          /^[6-9][0-9]{9}$/,     // XXXXXXXXXX (10 digits starting with 6-9)
        ];

        return [...uaePatterns, ...indianPatterns].some(pattern => pattern.test(cleaned));
      },
      "Please enter a valid phone number (UAE: 050 123 4567 or India: +91 98765 43210)"
    ),
  companyName: z.string().optional(),
  companyAddress: z.string().optional(),
  message: z.string().min(1, "Message is required").min(10, "Message must be at least 10 characters")
});

// type ContactFormData = z.infer<typeof contactFormSchema>;

const locationDetails = [
  {
    mainLocation: "Abu Dhabi",
    subLocation: [
      {
        locationCode: (
          <>
            Head Office - <br /> Abu Dhabi
          </>
        ),
        storeAddress: (
          <>
            Gemini Group Corporate Office: <br />
            Bin Ghanim Building, #14th Floor, <br />
            Hamdan Street, Opposite Nehal Hotel, <br />
            Abu Dhabi, UAE
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3630.6587032164516!2d54.374612299999995!3d24.4972796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e679a1faed6ad%3A0x29ac3a82530d908b!2sGemini%20Group%20Head%20Office!5e0!3m2!1sen!2sin!4v1748332059715!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2765.5408090906562!2d54.374612299999995!3d24.4972796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e679a1faed6ad%3A0x29ac3a82530d908b!2sGemini%20Group%20Head%20Office!5e1!3m2!1sen!2sin!4v1748272769935!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/J9qnrG683bNe5b7G7",
        pointOfContact: <></>,
        contactNo: <>+971 2 674 0505</>,
      },
      {
        locationCode: "Mussafah 1",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch 1: <br />
            Shop M, Firdous Gen. Cont. Est., <br />
            Mussafah, Abu Dhabi. <br />
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d908.573446084038!2d54.5159191!3d24.3710814!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e4105390f8a27%3A0x4b9683a19256f43e!2sGEMINI%20BUILDING%20MATERIALS%20OLD%20Shop%201st%20Branch!5e0!3m2!1sen!2sin!4v1748332124015!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d692.0774296343258!2d54.5159191!3d24.3710814!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e4105390f8a27%3A0x4b9683a19256f43e!2sGEMINI%20BUILDING%20MATERIALS%20OLD%20Shop%201st%20Branch!5e1!3m2!1sen!2sin!4v1748273033830!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/kSsB1iPBKYitJe878",
        pointOfContact: <>Sajith Chalakaran Karthyarath</>,
        contactNo: <>+971 50 416 5125</>,
      },
      {
        locationCode: "Mussafah 2",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch 2: <br />
            Mussafah Branch 2, M24: <br />
            Shop No. 1,2,3,4, <br />
            Near Ahalia Hospital, <br />
            Mussafah Industrial Area, <br />
            Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d58150.466476856396!2d54.4807565!3d24.3672401!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e473381d8474f%3A0x1b4126a28eaa072!2sGemini%20Building%20Materials%2C%20Mussafah!5e0!3m2!1sen!2sin!4v1748332188635!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44294.300636667955!2d54.4807565!3d24.3672401!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e473381d8474f%3A0x1b4126a28eaa072!2sGemini%20Building%20Materials%2C%20Mussafah!5e1!3m2!1sen!2sin!4v1748273140953!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/zUBq2fnEXnS64za47",
        pointOfContact: <>Nithin Mooliyil Rayaroth</>,
        contactNo: <>+971 55 265 4766</>,
      },
      {
        locationCode: "Mussafah 3",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch, M3: <br />
            Opposite Ali & Sons showroom, <br />
            Mussafah, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d58148.78302920023!2d54.4807565!3d24.370902!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e411a5702bd11%3A0x4d2e89a0c3f089ec!2sGemini%20Building%20Materials%2C%20Mussafah%203!5e0!3m2!1sen!2sin!4v1748332260384!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44293.018323023614!2d54.4807565!3d24.370902!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e411a5702bd11%3A0x4d2e89a0c3f089ec!2sGemini%20Building%20Materials%2C%20Mussafah%203!5e1!3m2!1sen!2sin!4v1748273294346!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/SwhjFeqHNUfw9iDm6",
        pointOfContact: <>Boni Edward Thedor</>,
        contactNo: <>+971 52 958 2708</>,
      },
      {
        locationCode: "Mussafah 9",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch, M9: <br />
            Next to Aramex, <br />
            Mussafah, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d58148.78302920023!2d54.4807565!3d24.370902!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e4175a4b48bef%3A0x4269e8f33725701f!2sGemini%20Building%20Materials%20Mussafah%20M9!5e0!3m2!1sen!2sin!4v1748332561676!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44293.018323023614!2d54.4807565!3d24.370902!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e4175a4b48bef%3A0x4269e8f33725701f!2sGemini%20Building%20Materials%20Mussafah%20M9!5e1!3m2!1sen!2sin!4v1748273407722!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/X7Fq5mTuxddLk63o9",
        pointOfContact: <>Nihal Backer</>,
        contactNo: <>+971 56 935 7237</>,
      },
      {
        locationCode: "Mussafah 21",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch, M21: <br />
            Office & Warehouse, <br />
            Mussafah, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d58147.307639311664!2d54.4401366!3d24.3741109!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e410c34195d47%3A0xafe9a708e548315f!2sGemini%20Building%20Materials%20Branch_Mussafah%2021!5e0!3m2!1sen!2sin!4v1748332621536!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44291.89449088193!2d54.4401366!3d24.3741109!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e410c34195d47%3A0xafe9a708e548315f!2sGemini%20Building%20Materials%20Branch_Mussafah%2021!5e1!3m2!1sen!2sin!4v1748273519545!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/R29tBwCfEiGTuNVi6",
        pointOfContact: <>Sreekanth Thothen Kaiprath</>,
        contactNo: <>+971 50 146 7276</>,
      },
      {
        locationCode: "Mussafah 37",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mussafah Branch, M37: <br />
            Ware House Building No. 1, <br />
            Sector 37, Plot 111, <br />
            Street Name M37, <br />
            Mussafah, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d58150.466476856396!2d54.4807565!3d24.3672401!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e40eeb3591d5d%3A0xa016df3f6f599025!2sGemini%20building%20material%20m-37!5e0!3m2!1sen!2sin!4v1748332689480!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44294.300636667955!2d54.4807565!3d24.3672401!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e40eeb3591d5d%3A0xa016df3f6f599025!2sGemini%20building%20material%20m-37!5e1!3m2!1sen!2sin!4v1748319163785!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/9hycUegYNvztJj1C6",
        pointOfContact: <>Kalesh Kanayi</>,
        contactNo: <>+971 58 164 7910</>,
      },
      {
        locationCode: "Mina 1",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mina Branch: <br />
            Abu Dhabi Island Sector, <br />
            Plot No. 432, Block E, Port Zayed, <br />
            Near Mina Coop, <br />
            Next To 1 to 10 Shop, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3629.998999778013!2d54.373791399999995!3d24.520117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e67a0ee61ab09%3A0x97f8f89c5e5b4fd7!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials-Mina%20abudhabi!5e0!3m2!1sen!2sin!4v1748332751262!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2765.0383006121583!2d54.373791399999995!3d24.520117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e67a0ee61ab09%3A0x97f8f89c5e5b4fd7!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials-Mina%20abudhabi!5e1!3m2!1sen!2sin!4v1748319289252!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/wzznQS8at4RaGmaj8",
        pointOfContact: <>Jithin Pappinisseri Veettil</>,
        contactNo: <>+971 55 699 0246</>,
      },
      {
        locationCode: "Mina 2",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Mina Branch: <br />
            Abu Dhabi Island Sector, <br />
            Plot No. 432, Block E, Port Zayed, <br />
            Near Mina Coop, <br />
            Next To 1 to 10 Shop, Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3629.998999778013!2d54.373791399999995!3d24.520117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e67a0ee61ab09%3A0x97f8f89c5e5b4fd7!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials-Mina%20abudhabi!5e0!3m2!1sen!2sin!4v1748332799430!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2765.0383006121583!2d54.373791399999995!3d24.520117!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e67a0ee61ab09%3A0x97f8f89c5e5b4fd7!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials-Mina%20abudhabi!5e1!3m2!1sen!2sin!4v1748319363371!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/wzznQS8at4RaGmaj8",
        pointOfContact: <>Jithin Pappinisseri Veettil</>,
        contactNo: <>+971 55 699 0246</>,
      },
      {
        locationCode: "Bada Zayed",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Bada Zayed Branch: <br />
            Shop No 3, 4 & Store, <br />
            Al Madina General Trading Building, <br />
            Sanaiya, Madinat Zayed, <br />
            Abu Dhabi.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3654.482283631401!2d53.7269583!3d23.658704899999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e6767855a39b909%3A0x94b20299acdd2ba0!2sGemini%20Building%20Materials%20Madinat%20Zayed!5e0!3m2!1sen!2sin!4v1748332873971!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2783.6876769848564!2d53.7269583!3d23.658704899999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e6767855a39b909%3A0x94b20299acdd2ba0!2sGemini%20Building%20Materials%20Madinat%20Zayed!5e1!3m2!1sen!2sin!4v1748319448715!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/xfWMfxtniLEnSDVC8",
        pointOfContact: <>Umashan Ajanur Gopalan</>,
        contactNo: <>+971 55 401 1290</>,
      },
    ],
  },
  {
    mainLocation: "Al-Ain",
    subLocation: [
      {
        locationCode: "Al Ain-1",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Al Ain Branch 1: <br />
            Plot No. 16, <br />
            Salim Owaidha Jaber Al Khaili Building, <br />
            Sanaya, Al Ain.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3639.3996799561796!2d55.7614722!3d24.1927778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDExJzM0LjAiTiA1NcKwNDUnNDEuMyJF!5e0!3m2!1sen!2sin!4v1748332991827!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2772.1989749666213!2d55.7614722!3d24.1927778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDExJzM0LjAiTiA1NcKwNDUnNDEuMyJF!5e1!3m2!1sen!2sin!4v1748321426479!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/pgAVyqXwwEqR5h8S9",
        pointOfContact: <>Manoj Kaiprath Kaiprath Punnakkal</>,
        contactNo: <>+971 50 839 2514</>,
      },
      {
        locationCode: "Al Ain-2",
        storeAddress: (
          <>
            Gemini Building Materials Al Ain Branch 2: Shop 4, Sanaya, Al Ain.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3639.425377232869!2d55.760013799999996!3d24.1918773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8ab7327700823f%3A0x1222262fe2df0c8f!2sGemini%20Building%20Materials%20Al%20Ain%20Branch%20-2!5e0!3m2!1sen!2sin!4v1748333053811!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2772.218549064099!2d55.760013799999996!3d24.1918773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8ab7327700823f%3A0x1222262fe2df0c8f!2sGemini%20Building%20Materials%20Al%20Ain%20Branch%20-2!5e1!3m2!1sen!2sin!4v1748321500659!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/8BRtmxQm1xeMVddy7",
        pointOfContact: <>Manoj Kaiprath Kaiprath Punnakkal</>,
        contactNo: <>+971 50 839 2514</>,
      },
    ],
  },
  {
    mainLocation: "Dubai",
    subLocation: [
      {
        locationCode: "Al Qusais",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Al Qusais Branch: <br />
            Land No. 2430217, Industrial Area 2, <br />
            Near Dubai Residential Oasis, <br />
            Damascus Street, <br />
            Al Qusais, Dubai.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.6481921486134!2d55.39125!3d25.2824175!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5c253fffffff%3A0x43ab521049ede07e!2sGEMINI%20BUILDING%20MATERIALS%20LLC!5e0!3m2!1sen!2sin!4v1748333115887!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2748.013271363202!2d55.39125!3d25.2824175!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5c253fffffff%3A0x43ab521049ede07e!2sGEMINI%20BUILDING%20MATERIALS%20LLC!5e1!3m2!1sen!2sin!4v1748319546495!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/noasezvc2BSUaGh48",
        pointOfContact: <>Ram Kumar Madasangili</>,
        contactNo: <>+971 50 924 4616</>,
      },
      {
        locationCode: "Al Aweer",
        storeAddress: (
          <>
            Gemini Building Materials, <br />
            Aweer Branch: <br />
            Plot No. 177-0, 19th Street, <br />
            Industrial Area No. 2, <br />
            Behind Tas-Heel, <br />
            Ras-Al-Khor,Dubai.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.959776302505!2d55.3507721!3d25.170835999999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f67e44d19d4b1%3A0xdb97d49f4e0b604f!2sGemini%20Building%20Materials%2C%20Ras%20Al%20Khor%20Branch!5e0!3m2!1sen!2sin!4v1748333171455!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2750.5357671054235!2d55.3507721!3d25.170835999999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f67e44d19d4b1%3A0xdb97d49f4e0b604f!2sGemini%20Building%20Materials%2C%20Ras%20Al%20Khor%20Branch!5e1!3m2!1sen!2sin!4v1748319626989!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/pDai2dMXEU7xTgNGA",
        pointOfContact: <>Santhosh Kadampat</>,
        contactNo: <>+971 50 924 4083</>,
      },
      {
        locationCode: "Al Quoz ",
        storeAddress: (
          <>
            Gemini Building Materials, <br />
            Al Qouz Branch: <br />
            Building No.46, Al Qouz Ind. Area No.4, <br />
            Near Emirates Glass, <br />
            Opposite NMC Warehouse, <br />
            Al Qouz, Dubai.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.70951197584!2d55.2329801!3d25.111692799999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f69580bf921b3%3A0x3ac0461ef87eea87!2sGEMINI%20BUILDING%20MATERIALS%20-%20AL%20QUOZ%20BRANCH!5e0!3m2!1sen!2sin!4v1748333232720!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2751.868573575347!2d55.2329801!3d25.111692799999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f69580bf921b3%3A0x3ac0461ef87eea87!2sGEMINI%20BUILDING%20MATERIALS%20-%20AL%20QUOZ%20BRANCH!5e1!3m2!1sen!2sin!4v1748319708267!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/gDGPdLfaJ9t4LgHm6",
        pointOfContact: <>Unnikrishna Pillai Ramakrishna Pillai</>,
        contactNo: <>+971 50 220 7804</>,
      },
      {
        locationCode: "Al khail ",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Al Khail Branch: <br />
            Al Quoz Industrial Area 1, <br />B Street 3 B, Dubai.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d57795.12512918386!2d55.2178274!3d25.1290866!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f696298bef3c5%3A0x7f21b111f3107c8b!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials%20Trading%20Al%20Quoz!5e0!3m2!1sen!2sin!4v1748333284426!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d44023.63046949552!2d55.2178274!3d25.1290866!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f696298bef3c5%3A0x7f21b111f3107c8b!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials%20Trading%20Al%20Quoz!5e1!3m2!1sen!2sin!4v1748319804009!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/6ihALmz7meUpVHZb9",
        pointOfContact: <>Sulfiker Arayilakath T K</>,
        contactNo: <>+971 50 220 5408</>,
      },
      {
        locationCode: "Umm Ramool",
        storeAddress: (
          <>
            Gemini Building Materials, <br />
            Umm Ramool Branch: <br />
            Plot No 288-0, <br />
            Umm-Ramool, Dubai
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3609.215442007533!2d55.361927!3d25.2296676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d543ebcc78d%3A0xbaccf6994e6a0515!2sGEMINI%20BUILDING%20MATERIALS%20LLC%20-%20UMM%20RAMOOL%20BRANCH!5e0!3m2!1sen!2sin!4v1748333336726!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2749.2070749666755!2d55.361927!3d25.2296676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d543ebcc78d%3A0xbaccf6994e6a0515!2sGEMINI%20BUILDING%20MATERIALS%20LLC%20-%20UMM%20RAMOOL%20BRANCH!5e1!3m2!1sen!2sin!4v1748319888240!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/sY8bNCzhqsEW89fEA",
        pointOfContact: <>Muhammad Jameel Ameer Amza</>,
        contactNo: <>+971 50 915 1676</>,
      },
      {
        locationCode: "Deira",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Deira Branch: <br />
            Showroom Cum Store, <br />
            Al Khabeesi Old Factory Building, <br />
            Deira, Dubai.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7216.045769347768!2d55.3387993!3d25.2698156!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5db5689053f1%3A0x937644a90186d912!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials%20Deira!5e0!3m2!1sen!2sin!4v1748333419103!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d5496.59736337037!2d55.3387993!3d25.2698156!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5db5689053f1%3A0x937644a90186d912!2sJOTUN%20PAINTS%20-%20Gemini%20Building%20Materials%20Deira!5e1!3m2!1sen!2sin!4v1748319967543!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/dEm82yF3EQMprwdK9",
        pointOfContact: <>Musthafa Cheruvayil A R</>,
        contactNo: <>+971 55 882 8883</>,
      },
    ],
  },
  {
    mainLocation: "Sharjah",
    subLocation: [
      {
        locationCode: "Sharjah 1",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Sharjah Branch 1: <br />
            Warehouse Cum Office, <br />
            Plot 624, Molhaiya Street, <br />
            Behind Al Ghurair Exchange, <br />
            Sanaya 5, Sharjah.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d461635.1806005994!2d55.3814675!3d25.3201634!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f595393f863c7%3A0xa6b5ff07f94726fd!2sGemini%20Building%20Materials%20(shj.%20Main%20branch)!5e0!3m2!1sen!2sin!4v1748333882260!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d351636.1727231128!2d55.3814675!3d25.3201634!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f595393f863c7%3A0xa6b5ff07f94726fd!2sGemini%20Building%20Materials%20(shj.%20Main%20branch)!5e1!3m2!1sen!2sin!4v1748320041193!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/cMCgkAcUWeurYrYd7",
        pointOfContact: <>Mohamed Ismayil Achipra Valappil</>,
        contactNo: <>+971 56 455 0200</>,
      },
      {
        locationCode: "Sharjah 2",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Sharjah Branch 2: <br />
            Store No 1, 20 & Showroom, <br />
            Abdul Aziz Al Majid Building, <br />
            Behind Pan Emriates Furnitures, <br />
            Industrial Area, Sharjah.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d28852.834601416896!2d55.3924568!3d25.3174946!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5be1f4dd32f3%3A0x48c91773a8f47e64!2sGEMINI%20BUILDING%20MATERIALS%20%2C%20SHARJAH%202!5e0!3m2!1sen!2sin!4v1748333937592!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d21977.745106548027!2d55.3924568!3d25.3174946!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5be1f4dd32f3%3A0x48c91773a8f47e64!2sGEMINI%20BUILDING%20MATERIALS%20%2C%20SHARJAH%202!5e1!3m2!1sen!2sin!4v1748320324545!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/DZS2BmjDMjjzZumT9",
        pointOfContact: <>Muhamed Hijas Kaneelagath</>,
        contactNo: <>+971 56 665 0517</>,
      },
      {
        locationCode: "Sharjah 3",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Sharjah Branch 3: <br />
            Sharjah Industrial Area 2, <br />
            Al Khan Street, Sharjah.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14428.083584234419!2d55.3924504!3d25.303502!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d6939aaa3fd%3A0x153a2a9aaab54e02!2sJOTUN%20PAINTS%20-%20GEMINI%20BUILDING%20MATERIALS%20SHARJAH%203!5e0!3m2!1sen!2sin!4v1748334003806!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d10990.141792678562!2d55.3924504!3d25.303502!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d6939aaa3fd%3A0x153a2a9aaab54e02!2sJOTUN%20PAINTS%20-%20GEMINI%20BUILDING%20MATERIALS%20SHARJAH%203!5e1!3m2!1sen!2sin!4v1748320391914!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/aSyRojbkP1SJyt398",
        pointOfContact: <>Ramachandran Nair Gopalakrishnan</>,
        contactNo: <>+971 56 990 6233</>,
      },
    ],
  },
  {
    mainLocation: "Ajman",
    subLocation: [
      {
        locationCode: "Ajman 1",
        storeAddress: (
          <>
            Gemini Building Materials, <br />
            Ajman Branch 1: <br />
            Showroom, Plot No. 365, <br />
            Industrial Area 1, Ajman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d57669.83972537869!2d55.4492596!3d25.3925906!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f587167792ded%3A0x73337e4eed197b0e!2sGemini%20Building%20Materials%20LLC%20Ajman%20Main%20Branch!5e0!3m2!1sen!2sin!4v1748334069110!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d43928.1982283158!2d55.4492596!3d25.3925906!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f587167792ded%3A0x73337e4eed197b0e!2sGemini%20Building%20Materials%20LLC%20Ajman%20Main%20Branch!5e1!3m2!1sen!2sin!4v1748320471900!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/7P285PdmwuqaFe736",
        pointOfContact: <>Anil Kumar Thattanchery</>,
        contactNo: <>+971 50 617 3848</>,
      },
      {
        locationCode: "Ajman 2",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            Ajman Branch 2: <br />
            Showroom With 2 Nos. <br />
            Store, Plot No. 0471, <br />
            Jurf 1, Industrial Area, <br />
            Opposite Tech Wood LLC, Ajman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d900.809749387007!2d55.526055!3d25.4302721!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f935d0bd074f%3A0x243d2e5b2a3d476b!2sGEMINI%20BUILDING%20MATERIALS%20LLC%2C%20Ajman(KNAUF%2CGYPROC%26GTI%20AUTHORISED%20DEALER)!5e0!3m2!1sen!2sin!4v1748334123887!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d686.1636762908843!2d55.526055!3d25.4302721!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f935d0bd074f%3A0x243d2e5b2a3d476b!2sGEMINI%20BUILDING%20MATERIALS%20LLC%2C%20Ajman(KNAUF%2CGYPROC%26GTI%20AUTHORISED%20DEALER)!5e1!3m2!1sen!2sin!4v1748320912287!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/6mzaG1v5CFaN8YVf8",
        pointOfContact: <>Prajith Madhavan Pillai</>,
        contactNo: <>+971 50 220 5469</>,
      },
    ],
  },
  {
    mainLocation: "Umm-Al-Quawain",
    subLocation: [
      {
        locationCode: "UAQ",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            UAQ Branch: <br />
            Plot No. 165, Block 23, <br />
            Area 2, Faisal King Road, <br />
            Old Sanaya, <br />
            Behind Nesto Supermarket, <br />
            Umm-Al-Quwain.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3601.169671001152!2d55.552713000000004!3d25.499387300000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f915af24d437%3A0xbbe2cd6af852baee!2sGemini%20Building%20Materials%20LLC%20-%20Umm%20Al%20Quwain!5e0!3m2!1sen!2sin!4v1748334190779!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2743.0784603329084!2d55.552713000000004!3d25.499387300000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f915af24d437%3A0xbbe2cd6af852baee!2sGemini%20Building%20Materials%20LLC%20-%20Umm%20Al%20Quwain!5e1!3m2!1sen!2sin!4v1748321179382!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/qrQ26xPhE1LByBvj6",
        pointOfContact: <>Akhilesh Kumar Punnackapalliyil Anirudhan</>,
        contactNo: <>+971 50 818 8439</>,
      },
    ],
  },
  {
    mainLocation: "Ras-Al-Khaimah",
    subLocation: [
      {
        locationCode: "Rak",
        storeAddress: (
          <>
            Gemini Building Materials <br />
            RAK Branch: <br />
            Plot No. 403020085, <br />
            Al Falaya Mainroad, Al Kharran, <br />
            Next to H2O Vehicle Service Center, <br />
            Ras-Al-Khaimah.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3594.1111330479766!2d55.98171060000001!3d25.7338382!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef67703b1a09b2f%3A0xaedc0bd40b075920!2sKnauf%20Gypsum%20Dealer%20(Gemini%20Building%20Materials%20LLC%20-%20RAK)!5e0!3m2!1sen!2sin!4v1748334273686!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2737.7018396263884!2d55.98171060000001!3d25.7338382!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef67703b1a09b2f%3A0xaedc0bd40b075920!2sKnauf%20Gypsum%20Dealer%20(Gemini%20Building%20Materials%20LLC%20-%20RAK)!5e1!3m2!1sen!2sin!4v1748321277307!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/HQXocHcdENDh8uCp8",
        pointOfContact: <>Frank Vallippalam Thomas</>,
        contactNo: <>+971 50 220 7802</>,
      },
    ],
  },
  {
    mainLocation: "Fujairah",
    subLocation: [
      {
        locationCode: "Fujairah",
        storeAddress: (
          <>
            Gemini Building Materials, <br />
            Fujairah Branch: <br />
            Plot No 9, Block A, <br />
            Opp. Grand Sheikh Zayed Mosque, <br />
            Mohamed Bin Matar Road, <br />
            Fujairah
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.236000816225!2d56.3240297!3d25.1277109!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef4f88d4bf376e1%3A0x7c3fee962b4e6ad!2sGemini%20Building%20Materials%20LLC!5e0!3m2!1sen!2sin!4v1748334331960!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2751.507891246734!2d56.3240297!3d25.1277109!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef4f88d4bf376e1%3A0x7c3fee962b4e6ad!2sGemini%20Building%20Materials%20LLC!5e1!3m2!1sen!2sin!4v1748321351782!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/RMsrAHYeb3Rid65b7",
        pointOfContact: <>Dhanesh Nellyarichalil</>,
        contactNo: <>+971 50 243 7335</>,
      },
    ],
  },
  {
    mainLocation: "Kuwait",
    subLocation: [
      {
        locationCode: "KUWAIT",
        storeAddress: (
          <>
            Gemini General Trading Co. <br />
            Branch 1: <br />
            63rd Street, <br />
            Shuwaikh Industrial Area, <br />
            Kuwait.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d6957.358417826553!2d47.9261553!3d29.3210842!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3fcf9b0c675e4639%3A0x1d0f46393ce6ee66!2zR0VNSU5JIEdFTkVSQUwgVFJBRElORyDYp9mE2KzZiNiy2KfYoSDZhNmE2KrYrNin2LHYqSDYp9mE2LnYp9mF2Kk!5e0!3m2!1sen!2sin!4v1748334393042!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d5299.55035732882!2d47.9261553!3d29.3210842!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3fcf9b0c675e4639%3A0x1d0f46393ce6ee66!2zR0VNSU5JIEdFTkVSQUwgVFJBRElORyDYp9mE2KzZiNiy2KfYoSDZhNmE2KrYrNin2LHYqSDYp9mE2LnYp9mF2Kk!5e1!3m2!1sen!2sin!4v1748321607163!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/JdUruioQARevD1Ya7",
        pointOfContact: <>Pramod Menon</>,
        contactNo: <>+965 6906 1117</>,
      },
      {
        locationCode: "KUWAIT",
        storeAddress: (
          <>
            Gemini General Trading Co. <br />
            Branch 2: <br />
            Shuwaikh Industrial Area 2, <br />
            Street No.16, PO. Box: 13903, <br />
            Kuwait.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d869.6654228084345!2d47.9290104!3d29.3215979!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3fcf9b0c675e4639%3A0x1d0f46393ce6ee66!2zR0VNSU5JIEdFTkVSQUwgVFJBRElORyDYp9mE2KzZiNiy2KfYoSDZhNmE2KrYrNin2LHYqSDYp9mE2LnYp9mF2Kk!5e0!3m2!1sen!2sin!4v1748334445170!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d662.4404587798622!2d47.9290104!3d29.3215979!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3fcf9b0c675e4639%3A0x1d0f46393ce6ee66!2zR0VNSU5JIEdFTkVSQUwgVFJBRElORyDYp9mE2KzZiNiy2KfYoSDZhNmE2KrYrNin2LHYqSDYp9mE2LnYp9mF2Kk!5e1!3m2!1sen!2sin!4v1748321666618!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/kSuBgcDy9Cv4Wg9X9",
        pointOfContact: <>Pramod Menon</>,
        contactNo: <>+965 6906 1117</>,
      },
    ],
  },
  {
    mainLocation: "Oman",
    subLocation: [
      {
        locationCode: "Ghala",
        storeAddress: (
          <>
            Gemini International LLC <br />
            Muscat Branch: <br />
            Ghala Heights, Building No. 2846, <br />
            Way No.52, Behind El Sukry Garage, <br />
            Muscat, Sultanate Of Oman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d467916.5151515876!2d58.107838!3d23.6187541!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8e01b88f9d35c3%3A0xda0520e89885b346!2zR2VtaW5pIEludGVybmF0aW9uYWwgTExDINin2YTYrNmI2LLYp9ihINin2YTYudin2YTZhdmK2Kkg2YHYsdi5INi62YTYpw!5e0!3m2!1sen!2sin!4v1748334545967!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d356420.7830256233!2d58.107838!3d23.6187541!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8e01b88f9d35c3%3A0xda0520e89885b346!2zR2VtaW5pIEludGVybmF0aW9uYWwgTExDINin2YTYrNmI2LLYp9ihINin2YTYudin2YTZhdmK2Kkg2YHYsdi5INi62YTYpw!5e1!3m2!1sen!2sin!4v1748321966706!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/EqzRqgYP8Mg8qW649",
        pointOfContact: (
          <>
            Sujith Balakrishnan <br /> Yaqoub Zayid Mohammed Al Balushi <br />{" "}
            Kalandar Basha Hanif
          </>
        ),
        contactNo: (
          <>
            +968 93 20 3977 <br /> +968 92 79 8449
          </>
        ),
      },
      {
        locationCode: "Sohar",
        storeAddress: (
          <>
            Gemini International LLC <br />
            Sohar Branch: <br />
            P.O.Box: 438, P.C: 321, <br />
            Muwalyah St. Sohar, <br />
            Sultanate Of Oman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3634.9855673760635!2d56.7077179!3d24.346995399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8b45c8fedcf3f5%3A0x758f73a62eced8f7!2sGemini%20International%20LLC!5e0!3m2!1sen!2sin!4v1748334616639!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2768.836662649736!2d56.7077179!3d24.346995399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8b45c8fedcf3f5%3A0x758f73a62eced8f7!2sGemini%20International%20LLC!5e1!3m2!1sen!2sin!4v1748322030427!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/h55PwRiQJ8V5eBBo9",
        pointOfContact: <>Radhakrishnan Sivakkolundhu</>,
        contactNo: (
          <>
            +968 93 20 3033 <br /> +968 93 91 9820
          </>
        ),
      },
      {
        locationCode: "Salalah",
        storeAddress: (
          <>
            Gemini International LLC <br />
            Salalah Branch: <br />
            Al Taqniyah St., Sannayah, <br />
            Salalah, <br />
            Sultanate Of Oman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3815.0615629004583!2d54.045636599999995!3d17.0206501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3dd15f412acb70d9%3A0x3527fc5eb9167d4f!2sGemini%20International%20LLC!5e0!3m2!1sen!2sin!4v1748334681410!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2906.0039248655835!2d54.045636599999995!3d17.0206501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3dd15f412acb70d9%3A0x3527fc5eb9167d4f!2sGemini%20International%20LLC!5e1!3m2!1sen!2sin!4v1748322199738!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/ztud8dtKe2tnTYkg7",
        pointOfContact: (
          <>
            Raneesh Chundaparambath <br /> Vishnu Madhusoodanan Nair
          </>
        ),
        contactNo: <>+968 99 87 6375</>,
      },
      {
        locationCode: "Mabilah",
        storeAddress: (
          <>
            Gemini International LLC <br />
            Mabilah Branch: <br />
            Al Salam St., Sannayah, <br />
            Mabilah, <br />
            Sultanate Of Oman.
          </>
        ),
        mapLink:
          "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3654.4434182375808!2d58.1057212!3d23.6600957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8de516e134f371%3A0xf903ea4a3b3fd580!2sGemini%20International%20LLC-%20Mabilah!5e0!3m2!1sen!2sin!4v1748334774590!5m2!1sen!2sin",
        // mapLink: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2783.658072485657!2d58.1057212!3d23.6600957!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e8de516e134f371%3A0xf903ea4a3b3fd580!2sGemini%20International%20LLC-%20Mabilah!5e1!3m2!1sen!2sin!4v1748322275858!5m2!1sen!2sin",
        // mapLink: "https://maps.app.goo.gl/DUz9NUuSnuhoW1MeA",
        pointOfContact: <>Binu Neelakantan</>,
        contactNo: <>+968 91 42 0646</>,
      },
    ],
  },
];

export default function ContactDetail() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentMapLink, setCurrentMapLink] = useState(
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3630.6587032164516!2d54.374612299999995!3d24.4972796!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5e679a1faed6ad%3A0x29ac3a82530d908b!2sGemini%20Group%20Head%20Office!5e0!3m2!1sen!2sin!4v1748325654352!5m2!1sen!2sin"
  );
  const router = useRouter();
  const [selectedList, setSelectedList] = useState<string>("Abu Dhabi");

  const [selectedLocation, setSelectedLocation] = useState<string>("Abu Dhabi");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Form state with server action
  const [state, formAction] = useActionState(sendEmail, { message: '', success: false });
  const { addToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);

  // Error state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle server action response
  useEffect(() => {
    if (state.message) {
      if (state.success) {
        // Clear form on success
        formRef.current?.reset();
        setErrors({});
        // Redirect to thank you page
        router.push('/thank-you');
      } else {
        addToast(state.message, 'error');
      }
    }
  }, [state, addToast, router]);

  // Handle input changes for validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle form submission with client-side validation
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // Get form data for validation
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      companyName: formData.get('companyName') as string,
      companyAddress: formData.get('companyAddress') as string,
      message: formData.get('message') as string
    };

    // Validate form data with Zod
    const result = contactFormSchema.safeParse(data);

    if (!result.success) {
      e.preventDefault(); // Prevent submission only if validation fails
      // Extract and set errors
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err: z.ZodIssue) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      addToast('Please fix the errors in the form', 'error');
      return;
    }

    // Clear any existing errors
    setErrors({});
    e.preventDefault(); // Prevent default submission to stop secondary trigger

    try {
      // 1. Call the backend API to save data
      await createContactUs(data);

      // addToast('Message sent successfully!', 'success');

      // Clear form on success
      formRef.current?.reset();
      setErrors({});



      // 2. If API success, trigger the Server Action to send email
      startTransition(() => {
        formAction(formData);
      });
      // Redirect to thank you page
      router.push('/thank-you');

    } catch (error) {
      console.error("API submission error:", error);
      addToast('Failed to submit form. Please try again.', 'error');
    }
  };

  // Get the sublocation data for the selected main location
  const selectedLocationData = locationDetails.find(
    (location) => location.mainLocation === selectedLocation
  );

  // Function to handle location selection
  const handleLocationSelect = (location: string) => {
    document.body.style.overflow = "auto";
    setSelectedLocation(location);
    setIsDropdownOpen(false);
    handleScroll();
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
    // Prevent body scroll when dropdown is open
    document.body.style.overflow = isDropdownOpen ? "auto" : " hidden";
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      scrollRef.current?.scrollIntoView({ behavior: "auto" });
    }
  };

  const handleTabSelection = (selectedTab: string) => {
    setSelectedList(selectedTab);
    handleScroll();
  };

  return (
    <>
      <Fade effect="fade">
        <div className="flex flex-col lg:flex-row w-full max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[76.05vw] mx-auto">
          <div className="flex-1">
            <div className="lg:max-w-[30.2vw] flex flex-col items-center lg:items-start">
              <div className="flex flex-col items-center lg:items-start gap-mob-20 sm:gap-tab-26 lg:gap-web-33">
                <h2 className="text-mob-32 sm:text-tab-36 lg:text-web-40 text-PrimaryLightBlue uppercase">
                  Head Office
                </h2>
                <p className="hidden sm:block selection:text-ParaLight text-center lg:text-start text-mob-20 sm:text-tab-20 lg:text-web-28">
                  {/* Gemini Building Materials, 14th Floor, Bin Ghanim Tower, Hamden
                Street, Tourist Club Area PO BOX 71295, Abu Dhabi U.A.E */}
                  Gemini Building Materials, <br />
                  14th Floor, Bin Ghanim Tower, Hamden Street, <br />
                  Tourist Club Area PO BOX 71295, Abu Dhabi <br />
                  United Arab Emirates
                </p>
                <p className="block sm:hidden text-ParaLight text-center lg:text-start text-mob-20 sm:text-tab-20 lg:text-web-28">
                  Gemini Building Materials <br />
                  14th Floor, Bin Ghanim Tower <br />
                  Hamden Street, Tourist Club Area <br />
                  PO BOX 71295 <br />
                  Abu Dhabi, United Arab Emirates
                </p>
              </div>

              <div className="my-mob-60 sm:my-tab-50 lg:my-web-70 w-[42%] sm:w-[35%] lg:w-[9.89vw]">
                <div className="relative block lg:hidden">
                  <Image
                    src="/images/theme-double-dot.svg"
                    alt="theme color"
                    fill
                    className="!static"
                  />
                </div>
                <div className="relative hidden lg:block">
                  <Image
                    src="/images/theme-dot-long.svg"
                    alt="theme color"
                    fill
                    className="!static"
                  />
                </div>
              </div>

              <p className="text-PrimaryLightBlue text-center lg:text-start uppercase lg:max-w-[19.63vw] text-mob-24 sm:text-tab-24 lg:text-web-28">
                For product and sales related queries:
              </p>

              <div className="flex flex-wrap justify-center lg:justify-start gap-mob-24 sm:gap-tab-24 lg:gap-web-40 mt-mob-20 sm:mt-tab-20 lg:mt-web-50">
                <div className="flex gap-4">
                  <div className="relative w-mob-24 sm:w-tab-24 lg:w-web-32">
                    <Image
                      src="/images/call-icon.svg"
                      alt="theme color"
                      fill
                      className="!static"
                    />
                  </div>
                  <p className="text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-28">
                    +971 2 6740505
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="relative w-mob-24 sm:w-tab-24 lg:w-web-32">
                    <Image
                      src="/images/fax-icon.svg"
                      alt="theme color"
                      fill
                      className="!static"
                    />
                  </div>
                  <p className="text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-28">
                    +971 2 6740506
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="relative w-mob-24 sm:w-tab-24 lg:w-web-32">
                    <Image
                      src="/images/mail-icon.svg"
                      alt="theme color"
                      fill
                      className="!static"
                    />
                  </div>
                  <p className="text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-28">
                    sales@geminiae.ae
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="mb-mob-45 mt-mob-96 sm:mt-tab-90 lg:mt-0 p-mob-22 sm:p-tab-22 lg:p-web-30 pt-mob-18 lg:pt-web-25 sm:pt-tab-18 rounded-mob-8 sm:rounded-tab-8 lg:rounded-web-8  bg-cover bg-center bg-no-repeat border border-PrimaryLightBlue lg:flex-1 lg:mb-0">
              <h4 className="text-PrimaryLightBlue text-mob-26 sm:text-tab-26 lg:text-web-28 font-normal leading-[120%] capitalize text-Black mb-mob-20 sm:mb-tab-23 lg:mb-web-34">
                Get In Touch
              </h4>
              <form ref={formRef} action={formAction} onSubmit={handleSubmit}>
                <ul className="flex flex-col gap-mob-18 sm:gap-tab-22 lg:gap-web-24">
                  {/* Name Field */}
                  <li>
                    <label
                      htmlFor="name"
                      className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                    >
                      Name*
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      className={`w-full h-mob-48 sm:h-tab-50 lg:h-web-52 bg-white border rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none px-mob-17 sm:px-tab-17 lg:px-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17 ${errors.name ? 'border-red-500' : 'border-[#e1e1e1]'
                        }`}
                    />
                    {errors.name && (
                      <p className="text-red-500 text-mob-14 sm:text-tab-14 lg:text-web-14 mt-mob-4 sm:mt-tab-4 lg:mt-web-4">
                        {errors.name}
                      </p>
                    )}
                  </li>

                  {/* Email and Phone Number */}
                  <li className="flex flex-col sm:flex-row gap-mob-18 sm:gap-tab-22 lg:gap-web-24">
                    <div className="flex-1">
                      <label
                        htmlFor="email"
                        className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                      >
                        Email Address*
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        className={`w-full h-mob-48 sm:h-tab-50 lg:h-web-52 bg-white border rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none px-mob-17 sm:px-tab-17 lg:px-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17 ${errors.email ? 'border-red-500' : 'border-[#e1e1e1]'
                          }`}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-mob-14 sm:text-tab-14 lg:text-web-14 mt-mob-4 sm:mt-tab-4 lg:mt-web-4">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="phone"
                        className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                      >
                        Phone Number*
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        className={`w-full h-mob-48 sm:h-tab-50 lg:h-web-52 bg-white border rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none px-mob-17 sm:px-tab-17 lg:px-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17 ${errors.phone ? 'border-red-500' : 'border-[#e1e1e1]'
                          }`}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-mob-14 sm:text-tab-14 lg:text-web-14 mt-mob-4 sm:mt-tab-4 lg:mt-web-4">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </li>

                  {/* Company Name and Company Address */}
                  <li className="flex flex-col sm:flex-row gap-mob-18 sm:gap-tab-22 lg:gap-web-24">
                    <div className="flex-1">
                      <label
                        htmlFor="companyName"
                        className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                      >
                        Company Name
                      </label>
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        onChange={handleInputChange}
                        placeholder="Enter your company name"
                        className="w-full h-mob-48 sm:h-tab-50 lg:h-web-52 bg-white border border-[#e1e1e1] rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none px-mob-17 sm:px-tab-17 lg:px-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="companyAddress"
                        className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                      >
                        Company Address
                      </label>
                      <Input
                        id="companyAddress"
                        name="companyAddress"
                        type="text"
                        onChange={handleInputChange}
                        placeholder="Enter your company address"
                        className="w-full h-mob-48 sm:h-tab-50 lg:h-web-52 bg-white border border-[#e1e1e1] rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none px-mob-17 sm:px-tab-17 lg:px-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17"
                      />
                    </div>
                  </li>

                  {/* Message */}
                  <li className="sm:mt-tab-20 lg:mt-web-20">
                    <label
                      htmlFor="message"
                      className="block text-ParaLight text-mob-20 sm:text-tab-20 lg:text-web-20 text-Black mb-mob-4 sm:mb-tab-4 lg:mb-web-4"
                    >
                      Type your message here*
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      onChange={handleInputChange}
                      placeholder="Type your message here"
                      className={`w-full h-[40vw] sm:h-[18vw] lg:h-[8vw] bg-white border rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-6 outline-none p-mob-17 sm:p-tab-17 lg:p-web-20 text-black text-mob-14 sm:text-tab-15 lg:text-web-17 ${errors.message ? 'border-red-500' : 'border-[#e1e1e1]'
                        }`}
                    />
                    {errors.message && (
                      <p className="text-red-500 text-mob-14 sm:text-tab-14 lg:text-web-14 mt-mob-4 sm:mt-tab-4 lg:mt-web-4">
                        {errors.message}
                      </p>
                    )}
                  </li>

                  {/* Send Button */}
                  <li>
                    <SubmitButton />
                  </li>
                </ul>
              </form>
            </div>
          </div>
        </div>
      </Fade>

      <Fade effect="fade">
        <section
          ref={scrollRef}
          id="store-location"
          className="flex flex-col w-full lg:flex-row lg:gap-web-36 lg:mt-[7.8vw]  max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[76.04vw] mx-auto"
        >
          <div className="order-2 lg:order-1  relative w-full ">
            <div className="">
              <div className="hidden lg:block">
                <Tabs
                  defaultValue="Abu Dhabhi"
                  value={selectedList}
                  onValueChange={(selectedTab) => handleTabSelection(selectedTab)}
                >
                  <div className="sticky top-0 bg-white z-10 pt-web-60">
                    <h1 className="uppercase text-left text-mob-32 sm:text-tab-32 lg:text-web-50 leading-[110%] lg:leading-[120%] text-PrimaryLightBlue  mb-mob-40 sm:mb-tab-40 lg:mb-web-50 ">
                      Locate our stores
                    </h1>
                    <div className="overflow-y-auto scrollbar-hide ">
                      <div className="relative mr-auto w-full">
                        <TabsList className="w-full items-center hidden sm:flex justify-between mb-10 lg:mb-web-32 gap-6 bg-transparent !shadow-none !p-0">
                          {locationDetails.map((details, index) => (
                            <TabsTrigger
                              key={index}
                              value={details.mainLocation}
                              className={`
                                        relative text-web-22 pb-2 pt-1 w-fit !shadow-none !border-0 !rounded-none !bg-transparent
                                        ${selectedList === details.mainLocation
                                  ? "!text-black font-normal"
                                  : "text-ParaLight font-normal hover:text-gray-800 text-web-22"
                                }
                                      `}
                            >
                              {details.mainLocation}

                              {/* Framer Motion Animated Underline */}
                              {selectedList === details.mainLocation && (
                                <motion.div
                                  layoutId="underline"
                                  className="absolute bottom-0 left-0 right-0 h-[4px] bg-PrimaryLightBlue"
                                  transition={{
                                    type: "spring",
                                    stiffness: 500,
                                    damping: 30,
                                  }}
                                />
                              )}
                            </TabsTrigger>
                          ))}
                        </TabsList>

                        {/* Static Yellow Line Across the Entire Bottom */}
                        <div className="absolute bottom-0 left-0 w-full h-[1px] !bg-SecondaryYellow"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="relative flex-1 flex-shrink-0">
                      {locationDetails.map((details, index) => (
                        <TabsContent
                          className="!mt-0"
                          key={index}
                          value={details.mainLocation}
                        >
                          <div className="flex flex-wrap mb-[11vw] gap-mob-40 sm:gap-tab-40 lg:gap-web-40">
                            {details.subLocation.map(
                              (subLocationDetails, index) => (
                                <LocationCard
                                  key={index}
                                  location={subLocationDetails.locationCode}
                                  address={subLocationDetails.storeAddress}
                                  mapLink={subLocationDetails.mapLink ?? ""}
                                  phone={subLocationDetails.contactNo}
                                  // latitude={subLocationDetails.latitude}
                                  // longitude={subLocationDetails.longitude}
                                  setCurrentMapLink={setCurrentMapLink}
                                />
                              )
                            )}
                          </div>
                        </TabsContent>
                      ))}
                    </div>
                    <div className="relative flex-1 flex-shrink-0 mt-mob-87 lg:mt-0 ">
                      <div className="lg:sticky rounded-web-10 overflow-hidden lg:top-[12.85vw] h-[calc(100dvh-14.5vw)]  after:content-[''] relative after:absolute after:left-[-51%] after:bottom-[-1vw] after:z-10 after:transform after:-translate-x-1/2 after:w-[100%]  after:h-[10vw]  after:bg-gradient-to-t after:from-white after:to-transparent  before:content-['']  before:absolute before:left-[-51%] before:bottom-[-3.9vw] before:z-10 before:transform before:-translate-x-1/2 before:w-[100%]  before:h-[4vw] before:bg-white  before:border-2 before:border-blue-800">
                          <iframe
                            src={currentMapLink}
                            title="Store Location Map Desktop"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            // className="w-full h-[93vw] sm:h-[70vw] lg:h-[32.66vw]"
                            className="w-full min-h-[100%]"
                          ></iframe>
                      </div>
                    </div>
                  </div>
                </Tabs>
              </div>

              {/* Mobile view tabs */}
              {/* <div className="lg:hidden">
              <div className="w-full">
                <Select
                  value={selectedLocation}
                  onValueChange={setSelectedLocation}
                >
                  <SelectTrigger className="w-full h-auto  !leading-none !text-mob-24 !sm:text-tab-24 !py-mob-12 !sm:py-tab-12 text-[#393939] font-normal px-mob-20 ">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent className="">
                    {locationDetails.map((location, index) => (
                      <SelectItem
                        key={index}
                        value={location.mainLocation}
                        className="text-mob-24 text-[#393939] cursor-pointer"
                      >
                        {location.mainLocation}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedLocationData && (
                <div className="flex flex-wrap justify-start gap-mob-40 sm:gap-tab-40 lg:gap-web-40 mt-mob-40">
                  {selectedLocationData.subLocation.map(
                    (subLocationDetails, index) => (
                      <LocationCard
                        key={index}
                        location={subLocationDetails.locationCode}
                        address={subLocationDetails.locationAddress}
                        phone={subLocationDetails.contactNo}
                        latitude={subLocationDetails.latitude}
                        longitude={subLocationDetails.longitude}
                        setCurrentMapLink={setCurrentMapLink}
                      />
                    )
                  )}
                </div>
              )}
            </div> */}
              <div className=" lg:hidden">
                <div className="sticky top-0 z-10 bg-white">
                  <iframe
                    src={currentMapLink}
                    title="Store Location Map Mobile"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full pt-mob-20 sm:pt-tab-20 h-[93vw] sm:h-[70vw] lg:h-[41.66vw]"
                  ></iframe>
                  <h1 className="uppercase text-center text-mob-32 sm:text-tab-32 lg:text-web-50 leading-[110%] lg:leading-[120%] text-PrimaryLightBlue  pt-mob-25 sm:pt-tab-20 pb-mob-10 sm:pb-tab-10">
                    Locate our stores
                  </h1>
                  {/* Custom Dropdown */}
                  <div className="relative w-full">
                    {/* Dropdown Toggle Button */}
                    <div
                      className="flex justify-between items-center px-mob-20 py-mob-12 border-b-2 border-PrimaryLightBlue cursor-pointer"
                      onClick={handleDropdownToggle}
                    >
                      <span className="text-mob-24 sm:text-tab-24 text-[#393939] font-normal">
                        {selectedLocation}
                      </span>
                      <div className="relative">
                        <Image
                          src="/images/chevron-down-blue.svg"
                          alt="arrow"
                          width={12}
                          height={12}
                          className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""
                            }`}
                        />
                      </div>
                    </div>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute max-h-[30dvh] overflow-auto z-10 w-full mt-1 bg-white border border-gray-200 shadow-lg rounded-md">
                        {locationDetails.map((location, index) => (
                          <div
                            key={index}
                            className="px-mob-20 py-mob-12 text-mob-24 sm:text-tab-24 text-[#393939] hover:bg-gray-100 cursor-pointer"
                            onClick={() =>
                              handleLocationSelect(location.mainLocation)
                            }
                          >
                            {location.mainLocation}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Cards */}
                {selectedLocationData && (
                  <div className="flex flex-wrap justify-start gap-mob-40 sm:gap-tab-40 lg:gap-web-40 mt-mob-40">
                    {selectedLocationData.subLocation.map(
                      (subLocationDetails, index) => (
                        <LocationCard
                          key={index}
                          location={subLocationDetails.locationCode}
                          address={subLocationDetails.storeAddress}
                          phone={subLocationDetails.contactNo}
                          mapLink={subLocationDetails.mapLink ?? ""}
                          // latitude={subLocationDetails.latitude}
                          // longitude={subLocationDetails.longitude}
                          setCurrentMapLink={setCurrentMapLink}
                        />
                      )
                    )}
                  </div>
                )}
                <div className="sticky bottom-0 left-0 w-full h-[30vw] sm:h-[20vw] lg:h-[10vw] pointer-events-none bg-gradient-to-t from-white to-transparent" />
              </div>
            </div>
          </div>
        </section>
      </Fade>
    </>
  );
}
